@if (error) {
  <mbs-alert
    class="mb-4"
    [icon]="true"
    [type]="MbsPopupType.danger">
    {{ error }}
  </mbs-alert>
}

@if (mode === CreationMode.Create && useExistingControl && canSelectSource) {
  <mbs-group-button
    class="w-100 mb-4"
    customClasses="w-100"
    type="secondary">
    <mbs-button
      class="flex-basis-50"
      customClasses="w-100 justify-content-center"
      [active]="useExistingControl.value"
      (click)="useExistingControl.setValue(true)">
      {{ 'storages:addStorageAccountWizard.inputCredentialsStepSource.selectExisting' | i18next }}
    </mbs-button>
    <mbs-button
      class="flex-basis-50"
      customClasses="w-100 justify-content-center"
      [active]="!useExistingControl.value"
      (click)="useExistingControl.setValue(false)">
      {{ 'storages:addStorageAccountWizard.inputCredentialsStepSource.createNew' | i18next }}
    </mbs-button>
  </mbs-group-button>
}

@if (!sourceReady) {
  <mbs-loader></mbs-loader>
} @else if (useExistingControl?.value) {
  <mbs-table-grid
    [bindSelected]="'id'"
    [data]="existingAccounts"
    [headers]="existingAccountsHeaders"
    [infiniteScroll]="true"
    [infiniteScrollDistance]="infiniteScrollDistance"
    [infiniteScrollLoading]="existingLoading"
    [infiniteScrollThrottle]="infiniteScrollThrottle"
    [keepState]="true"
    [maxHeight]="'min(500px, 45vh)'"
    [multipleSelect]="false"
    [selectable]="true"
    [selectedItems]="getSelectedExistingAccounts()"
    [serverSidePagination]="false"
    [showHeaders]="true"
    [showNav]="false"
    [showTableCheckboxes]="false"
    (changeSelected)="onExistingAccountSelected($event)"
    (scrolled)="loadMoreExisting.emit()">
    <ng-template
      let-account
      mbsTableCell>
      <div class="d-flex align-items-center overflow-hidden">
        <span class="mr-2 mbs-storage mbs-storage-sm mbs-storage_{{ account.storageType | storageIcon }}"></span>
        <mbs-text-ellipsis
          container="body"
          placement="top-left"
          [tooltip]="account.displayName">
          {{ account.displayName }}
        </mbs-text-ellipsis>
      </div>
    </ng-template>
    <ng-template
      let-account
      mbsTableCell>
      <mbs-text-ellipsis
        container="body"
        placement="top-left"
        [tooltip]="account.cloudStorageId">
        {{ account.cloudStorageId || '-' }}
      </mbs-text-ellipsis>
    </ng-template>
    <ng-template
      let-account
      mbsTableCell>
      <mbs-date-time-format [time]="account.dateCreated"></mbs-date-time-format>
    </ng-template>
  </mbs-table-grid>
} @else {
  <mbs-card class="card-form">
    <mbs-card-header>
      <ng-template>
        <div class="w-100 d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <span class="mbs-storage mbs-storage-sm mbs-storage_{{ storageType | storageIcon }}"></span>
            <h4 class="my-0 ml-2">{{ storageType | storageDisplayName }}</h4>
          </div>
          @if (helpLinkValue()) {
            <a
              target="_blank"
              [attr.data-e2e]="e2eLocators.howToSignUp"
              [helpLink]="helpLinkValue()">
              {{ 'storages:addStorageAccountWizard.inputCredentialsStep.howToSignUp' | i18next }}
            </a>
          }
        </div>
      </ng-template>
    </mbs-card-header>
    <div [class.border-bottom]="storageType !== StorageType.UbAmazonS3 && storageType !== StorageType.CblWasabi">
      <mbs-card-group>
        <mbs-input
          [attr.data-e2e]="e2eLocators.storageDisplayName"
          [boldLabel]="true"
          [formControl]="accountDisplayName"
          [label]="'storages:addStorageAccountWizard.inputCredentialsStep.labels.displayName' | i18next"
          [placeholder]="'storages:addStorageAccountWizard.inputCredentialsStep.placeholders.displayName' | i18next"
          [showRequiredMark]="false"></mbs-input>
      </mbs-card-group>
    </div>
    @switch (storageType) {
      @case (StorageType.Wasabi) {
        <mbs-credential-form-wasabi
          [form]="getForm(StorageType.Wasabi)"
          [mode]="mode"></mbs-credential-form-wasabi>
      }
      @case (StorageType.S3Compatible) {
        <mbs-credential-form-s3-compatible
          [form]="getForm(StorageType.S3Compatible)"
          [isNativeMultipartControlShown]="isNativeMultipartControlShown"
          [mode]="mode"
          [storageType]="StorageType.S3Compatible"></mbs-credential-form-s3-compatible>
      }
      @case (StorageType.Minio) {
        <mbs-credential-form-s3-compatible
          [form]="getForm(StorageType.Minio)"
          [isNativeMultipartControlShown]="isNativeMultipartControlShown"
          [mode]="mode"
          [storageType]="StorageType.Minio"></mbs-credential-form-s3-compatible>
      }
      @case (StorageType.GoogleCloud) {
        <mbs-credential-form-google-cloud
          [form]="getForm(StorageType.GoogleCloud)"
          [mode]="mode"></mbs-credential-form-google-cloud>
      }
      @case (StorageType.Google) {
        <mbs-credential-form-google-cloud
          [form]="getForm(StorageType.Google)"
          [mode]="mode"></mbs-credential-form-google-cloud>
      }
      @case (StorageType.LocalFS) {
        <mbs-credential-form-file-system
          [form]="getForm(StorageType.LocalFS)"
          [mode]="mode"></mbs-credential-form-file-system>
      }
      @case (StorageType.BackblazeB2) {
        <mbs-credential-form-backblaze [form]="getForm(StorageType.BackblazeB2)"></mbs-credential-form-backblaze>
      }
      @case (StorageType.AzureVM) {
        <mbs-credential-form-azure-vm
          [form]="getForm(StorageType.AzureVM)"
          [mode]="mode"></mbs-credential-form-azure-vm>
      }
      @case (StorageType.Azure) {
        <mbs-credential-form-azure [form]="getForm(StorageType.Azure)"></mbs-credential-form-azure>
      }
      @case (StorageType.AmazonS3) {
        <mbs-credential-form-s3
          [form]="getForm(StorageType.AmazonS3)"
          [mode]="mode"></mbs-credential-form-s3>
      }
      @case (StorageType.AmazonS3Eu) {
        <mbs-credential-form-s3
          [form]="getForm(StorageType.AmazonS3)"
          [mode]="mode"></mbs-credential-form-s3>
      }
      @case (StorageType.AmazonS3Chinese) {
        <mbs-credential-form-s3
          [form]="getForm(StorageType.AmazonS3)"
          [mode]="mode"></mbs-credential-form-s3>
      }
      @case (StorageType.FTP) {
        <mbs-credential-form-ftp [form]="getForm(StorageType.FTP)"></mbs-credential-form-ftp>
      }
      @case (StorageType.SFTP) {
        <mbs-credential-form-sftp [form]="getForm(StorageType.SFTP)"></mbs-credential-form-sftp>
      }
      @case (StorageType.OpenStack) {
        <mbs-credential-form-openstack [form]="getForm(StorageType.OpenStack)"></mbs-credential-form-openstack>
      }
      @case (StorageType.IDriveE2) {
        <mbs-credential-form-idrive [form]="getForm(StorageType.IDriveE2)"> </mbs-credential-form-idrive>
      }
      @case (StorageType.GoogleDriveOAuth) {
        <mbs-credential-form-google-drive [form]="getForm(StorageType.GoogleDriveOAuth)"> </mbs-credential-form-google-drive>
      }
      @case (StorageType.GoogleDrive) {
        <mbs-credential-form-google-drive [form]="getForm(StorageType.GoogleDrive)"> </mbs-credential-form-google-drive>
      }
      @case (StorageType.ImpossibleCloud) {
        <mbs-credential-form-impossible-cloud [form]="getForm(StorageType.ImpossibleCloud)"> </mbs-credential-form-impossible-cloud>
      }
    }
  </mbs-card>
}
